import type { PaddleCheckoutBootstrap } from "@shared/paddle-api";
import { withoutPaddleTransaction } from "./paddlePaymentLink";

type PaddleEvent = { name?: string; data?: { transaction_id?: string } };
type PaddleClient = {
  Environment: { set: (environment: "sandbox") => void };
  Initialize: (options: { token: string; eventCallback: (event: PaddleEvent) => void }) => void;
  Checkout: { open: (options: { transactionId: string;
    settings: { allowLogout: false; showAddDiscounts: false } }) => void };
};

declare global {
  interface Window {
    Paddle?: PaddleClient;
  }
}

let loadPromise: Promise<PaddleClient> | null = null;
let initializedToken: string | null = null;
let latestCallback: ((event: PaddleEvent) => void) | null = null;

async function loadPaddle(): Promise<PaddleClient> {
  if (window.Paddle) return window.Paddle;
  if (!loadPromise) {
    loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
      script.async = true;
      script.dataset.paddleJs = "true";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Paddle could not be loaded."));
      document.head.appendChild(script);
    }).then(() => {
      if (!window.Paddle) throw new Error("Paddle checkout is unavailable.");
      return window.Paddle;
    }).catch(error => {
      loadPromise = null;
      throw error;
    });
  }
  return loadPromise;
}

export async function openPaddleCheckout(bootstrap: PaddleCheckoutBootstrap, callback: (event: PaddleEvent) => void,
  isCurrent: () => boolean = () => true) {
  // Never allow an untrusted query string to open a different transaction when
  // Paddle.js initializes (including on ordinary Pricing/Account entry points).
  if (new URLSearchParams(window.location.search).has("_ptxn"))
    window.history.replaceState(window.history.state, "", withoutPaddleTransaction(window.location.href));
  const paddle = await loadPaddle();
  if (!isCurrent()) return;
  if (bootstrap.environment === "sandbox") paddle.Environment.set("sandbox");
  latestCallback = event => {
    if (event.data?.transaction_id && event.data.transaction_id !== bootstrap.transactionId) return;
    callback(event);
  };
  if (!initializedToken) {
    paddle.Initialize({ token: bootstrap.clientToken, eventCallback: event => latestCallback?.(event) });
    initializedToken = bootstrap.clientToken;
  } else if (initializedToken !== bootstrap.clientToken) {
    throw new Error("Paddle checkout configuration changed. Reload the page and try again.");
  }
  paddle.Checkout.open({ transactionId: bootstrap.transactionId,
    settings: { allowLogout: false, showAddDiscounts: false } });
}
